"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { Icon } from "@/components/formatters/MXPIcon";
import { MarketplaceCard, type MarketplaceCardProps } from "@/components/client/MarketplaceCard/MarketplaceCard";
import { FilterableCard, type FilterableCardProps } from "./FilterableCard";
import { TerraLink } from "../../component/TerraLink/TerraLink";
import { CommonDescription } from "../../component/CommonDescription/CommonDescription";
import { layoutHeaderClasses, layoutSubheaderClasses, layoutEyebrowClasses } from "@/components/tailwindHelpers";
import { darkTextureWrapper, colorSettingDetection, renderHeaderConditionally } from "../../utils/functions";
import { useScreenSize } from "../../utils/hooks/useScreenSize";
import { FilterSidebar } from "./FilterSidebar";
import { Pagination } from "./Pagination";
import { MobileFilterDrawer } from "./MobileFilterDrawer";
import type { FilterGroup, SortOption } from "./types";
import type { FilterableCardsLabelsFields } from "@/queries/fragments/filterableCardsLabelsFragment";
export type { FilterGroup, SortOption, FilterOption } from "./types";
export type { FilterableCardProps } from "./FilterableCard";
import "./LayoutFilterableCards.css";

/**
 * Display type values authored in Contentful on `layoutFilterableCards`.
 * Determines which card component (and matching prop shape) the grid renders.
 */
export type LayoutFilterableCardsDisplayType = "Filterable Card" | "Page: General";

/**
 * One grid item. The shape depends on the layout's {@link LayoutFilterableCardsProps.displayType}:
 * - `"Filterable Card"` → {@link FilterableCardProps} (renders {@link FilterableCard})
 * - `"Page: General"` (default) → {@link MarketplaceCardProps} (renders {@link MarketplaceCard})
 */
export type LayoutFilterableCardItem = FilterableCardProps | MarketplaceCardProps;
export interface LayoutFilterableCardsProps {
  eyebrow?: string;
  header?: string;
  headerElement?: "h1" | "h2";
  subheader?: string;
  description?: string;
  buttons?: TerraLink[];
  presetTheme?: "t2-theme-one" | "t2-theme-two" | "t2-theme-three" | "t2-theme-four" | "t2-theme-five" | "t2-theme-six" | "t2-theme-seven";
  isHeaderBig?: boolean;
  isWrapped?: boolean;

  /**
   * Default {@link MarketplaceCard} image mode when a card omits `imageDisplayMode`.
   * @default "logo"
   */
  imageDisplayMode?: "logo" | "teaser";
  cards?: LayoutFilterableCardItem[];
  totalResults: number;
  filterGroups?: FilterGroup[];
  activeFilters?: Record<string, string[]>;
  onFilterChange?: (groupId: string, values: string[]) => void;
  onClearAllFilters?: () => void;
  sortOptions?: SortOption[];
  currentSort?: string;
  onSortChange?: (value: string) => void;
  currentPage?: number;
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (size: number) => void;

  /**
   * Opt-in: drops the 1440px outer max-width, reduces large-screen horizontal
   * padding, and unlocks a 4-column grid at the `2xl` breakpoint (≥1536px).
   * Intended for the in-product iframe embed where the host page (e.g.
   * Trimble Connect) wants the catalog to fill more horizontal space on large
   * monitors. Defaults to false so public marketplace pages and Storybook
   * stories keep their existing capped layout.
   */
  fullWidth?: boolean;
  /**
   * Optional client-side search input rendered above the grid. When
   * `onSearchChange` is omitted the input is not rendered.
   */
  searchTerm?: string;
  onSearchChange?: (term: string) => void;
  searchPlaceholder?: string;
  isLCP?: boolean;
  /**
   * Selects which card component renders inside the grid.
   * - `"Filterable Card"` → {@link FilterableCard} (cards typed as {@link FilterableCardProps})
   * - `"Page: General"` (default) → {@link MarketplaceCard} (cards typed as {@link MarketplaceCardProps})
   */
  displayType?: LayoutFilterableCardsDisplayType;
  /** Filterable cards labels from `siteConfig.filterableCardsLabels`. */
  labels?: FilterableCardsLabelsFields | null;
}
const CARD_WRAP = "flex h-full w-full mb-6 max-w-[450px] justify-center items-stretch";
const SORT_SELECT = "border border-gray-m3 rounded-lg px-3 py-2 text-sm bg-white text-gray-m9 cursor-pointer min-w-[200px] appearance-none";
export const LayoutFilterableCards = t0 => {
  const $ = _c(106);
  const {
    eyebrow,
    header,
    headerElement: t1,
    subheader,
    description,
    buttons,
    presetTheme,
    isWrapped,
    imageDisplayMode: t2,
    cards: t3,
    totalResults,
    filterGroups: t4,
    activeFilters: t5,
    onFilterChange,
    onClearAllFilters,
    sortOptions: t6,
    currentSort,
    onSortChange,
    currentPage: t7,
    pageSize: t8,
    pageSizeOptions,
    onPageChange,
    onPageSizeChange,
    searchTerm,
    onSearchChange,
    searchPlaceholder,
    isLCP: t9,
    displayType,
    labels
  } = t0;
  const headerElement = t1 === undefined ? "h2" : t1;
  const imageDisplayMode = t2 === undefined ? "logo" : t2;
  let t10;
  if ($[0] !== t3) {
    t10 = t3 === undefined ? [] : t3;
    $[0] = t3;
    $[1] = t10;
  } else {
    t10 = $[1];
  }
  const cards = t10;
  let t11;
  if ($[2] !== t4) {
    t11 = t4 === undefined ? [] : t4;
    $[2] = t4;
    $[3] = t11;
  } else {
    t11 = $[3];
  }
  const filterGroups = t11;
  let t12;
  if ($[4] !== t5) {
    t12 = t5 === undefined ? {} : t5;
    $[4] = t5;
    $[5] = t12;
  } else {
    t12 = $[5];
  }
  const activeFilters = t12;
  let t13;
  if ($[6] !== t6) {
    t13 = t6 === undefined ? [] : t6;
    $[6] = t6;
    $[7] = t13;
  } else {
    t13 = $[7];
  }
  const sortOptions = t13;
  const currentPage = t7 === undefined ? 1 : t7;
  const pageSize = t8 === undefined ? 30 : t8;
  const isLCP = t9 === undefined ? false : t9;
  const resolvedSearchPlaceholder = searchPlaceholder ?? (labels?.labelForSearchApps || "Search apps...");
  const {
    width
  } = useScreenSize();
  const isMobile = width > 0 && width < 1024;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("filter");
  const wrapperClasses = isWrapped ? "w-full px-5 lg:px-20 m-auto max-w-[1440px]" : "";
  const t14 = presetTheme || "";
  let t15;
  if ($[8] !== t14) {
    t15 = colorSettingDetection(t14);
    $[8] = t14;
    $[9] = t15;
  } else {
    t15 = $[9];
  }
  const colorSetting = t15;
  const mainClasses = `py-16 lg:py-[100px] ${presetTheme || ""} ${colorSetting}`;
  const headerClasses = `${layoutHeaderClasses} mb-4! `;
  const t16 = header || "";
  let t17;
  if ($[10] !== headerElement || $[11] !== t16) {
    t17 = renderHeaderConditionally(headerClasses, t16, headerElement);
    $[10] = headerElement;
    $[11] = t16;
    $[12] = t17;
  } else {
    t17 = $[12];
  }
  const finalHeader = t17;
  const t18 = presetTheme || "";
  let t19;
  if ($[13] !== t18) {
    t19 = darkTextureWrapper(t18);
    $[13] = t18;
    $[14] = t19;
  } else {
    t19 = $[14];
  }
  const componentStyles = t19;
  const hasFilters = filterGroups.length > 0;
  const hasHeader = eyebrow || header || subheader || description;
  const isFilterableVariant = displayType === "Filterable Card";
  let t20;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = mode => {
      setDrawerMode(mode);
      setDrawerOpen(true);
    };
    $[15] = t20;
  } else {
    t20 = $[15];
  }
  const openDrawer = t20;
  let t21;
  if ($[16] !== imageDisplayMode || $[17] !== isFilterableVariant || $[18] !== isLCP || $[19] !== labels?.labelForFeatured || $[20] !== labels?.labelForFeaturedCard) {
    t21 = (card, index) => <div key={`card-${index}`} className={CARD_WRAP} data-testid="filterable-card">{isFilterableVariant ? <FilterableCard {...card as FilterableCardProps} featuredLabel={labels?.labelForFeatured || "Featured"} featuredCardAriaLabel={labels?.labelForFeaturedCard || "Featured card"} /> : <MarketplaceCard {...card as MarketplaceCardProps} imageDisplayMode={(card as MarketplaceCardProps).imageDisplayMode ?? imageDisplayMode} isLCP={isLCP} />}</div>;
    $[16] = imageDisplayMode;
    $[17] = isFilterableVariant;
    $[18] = isLCP;
    $[19] = labels?.labelForFeatured;
    $[20] = labels?.labelForFeaturedCard;
    $[21] = t21;
  } else {
    t21 = $[21];
  }
  const renderCard = t21;
  let t22;
  if ($[22] !== buttons || $[23] !== currentSort || $[24] !== description || $[25] !== eyebrow || $[26] !== finalHeader || $[27] !== hasHeader || $[28] !== isMobile || $[29] !== labels?.labelForResult || $[30] !== labels?.labelForResults || $[31] !== labels?.labelForSortBy || $[32] !== onSearchChange || $[33] !== onSortChange || $[34] !== sortOptions || $[35] !== subheader || $[36] !== totalResults) {
    t22 = hasHeader && <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between pb-8 lg:pb-12 gap-4"><div className="flex-1">{eyebrow && <div className={layoutEyebrowClasses}>{eyebrow}</div>}<div className="flex items-baseline gap-2 flex-wrap">{finalHeader}{totalResults > 0 && !onSearchChange && <span className="text-gray-m5 t2-dark:text-gray-m3 text-lg font-normal">({totalResults}{" "}{totalResults === 1 ? labels?.labelForResult || "Result" : labels?.labelForResults || "Results"})</span>}</div>{subheader && <div className={layoutSubheaderClasses}>{subheader}</div>}{description && <CommonDescription description={description} className="t2-layout__description mt-2" />}{buttons && buttons.length > 0 && <div className="flex gap-x-4 gap-y-3 flex-wrap mt-4">{buttons.map(_temp)}</div>}</div>{sortOptions.length > 0 && !isMobile && <div className="shrink-0 flex items-center gap-2"><label htmlFor="filterable-sort" className="text-sm text-gray-m7 t2-dark:text-gray-m3 whitespace-nowrap">{labels?.labelForSortBy || "Sort By"}:</label><div className="relative"><select id="filterable-sort" className={SORT_SELECT} value={currentSort} onChange={e => onSortChange?.(e.target.value)}>{sortOptions.map(_temp2)}</select><Icon type="t2u-chevron-right" size={10} rotateDeg="90" className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-m5 t2-dark:text-gray-m3" /></div></div>}</div>;
    $[22] = buttons;
    $[23] = currentSort;
    $[24] = description;
    $[25] = eyebrow;
    $[26] = finalHeader;
    $[27] = hasHeader;
    $[28] = isMobile;
    $[29] = labels?.labelForResult;
    $[30] = labels?.labelForResults;
    $[31] = labels?.labelForSortBy;
    $[32] = onSearchChange;
    $[33] = onSortChange;
    $[34] = sortOptions;
    $[35] = subheader;
    $[36] = totalResults;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] !== activeFilters || $[39] !== filterGroups || $[40] !== hasFilters || $[41] !== isMobile || $[42] !== labels || $[43] !== onClearAllFilters || $[44] !== onFilterChange) {
    t23 = hasFilters && !isMobile && <FilterSidebar filterGroups={filterGroups} activeFilters={activeFilters} onFilterChange={(groupId, values) => onFilterChange?.(groupId, values)} onClearAll={() => onClearAllFilters?.()} labels={labels} />;
    $[38] = activeFilters;
    $[39] = filterGroups;
    $[40] = hasFilters;
    $[41] = isMobile;
    $[42] = labels;
    $[43] = onClearAllFilters;
    $[44] = onFilterChange;
    $[45] = t23;
  } else {
    t23 = $[45];
  }
  let t24;
  if ($[46] !== labels?.labelForResult || $[47] !== labels?.labelForResults || $[48] !== onSearchChange || $[49] !== resolvedSearchPlaceholder || $[50] !== searchTerm || $[51] !== totalResults) {
    t24 = (onSearchChange || totalResults > 0) && <div className="flex items-center justify-between gap-4 mb-6">{onSearchChange ? <div className="relative flex-1 max-w-[480px]"><Icon type="t2u-search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-m5 t2-dark:text-gray-m3" /><input type="text" role="searchbox" aria-label={resolvedSearchPlaceholder} placeholder={resolvedSearchPlaceholder} value={searchTerm ?? ""} onChange={e_0 => onSearchChange(e_0.target.value)} className="w-full pl-9 pr-3 py-2 text-sm bg-white t2-dark:bg-gray-m9 text-gray-m9 t2-dark:text-white placeholder-gray-m5 border border-gray-m3 t2-dark:border-gray-m7 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-trmb focus:border-blue-trmb" /></div> : <span aria-hidden={true} />}{totalResults > 0 && <span className="text-sm text-gray-m5 t2-dark:text-gray-m3 whitespace-nowrap">{totalResults}{" "}{totalResults === 1 ? labels?.labelForResult || "Result" : labels?.labelForResults || "Results"}</span>}</div>;
    $[46] = labels?.labelForResult;
    $[47] = labels?.labelForResults;
    $[48] = onSearchChange;
    $[49] = resolvedSearchPlaceholder;
    $[50] = searchTerm;
    $[51] = totalResults;
    $[52] = t24;
  } else {
    t24 = $[52];
  }
  let t25;
  if ($[53] !== cards || $[54] !== labels?.labelForNoFilterResults || $[55] !== renderCard) {
    t25 = cards.length === 0 ? <div className="py-16 text-center text-gray-m5 t2-dark:text-gray-m3 text-lg">{labels?.labelForNoFilterResults || "No results found. Try adjusting your filters."}</div> : <div className="w-full grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 md:gap-x-6 xl:gap-x-8 2xl:gap-x-10 gap-y-4">{cards.map((card_0, index_0) => renderCard(card_0, index_0))}</div>;
    $[53] = cards;
    $[54] = labels?.labelForNoFilterResults;
    $[55] = renderCard;
    $[56] = t25;
  } else {
    t25 = $[56];
  }
  let t26;
  if ($[57] !== currentPage || $[58] !== labels || $[59] !== onPageChange || $[60] !== onPageSizeChange || $[61] !== pageSize || $[62] !== pageSizeOptions || $[63] !== totalResults) {
    t26 = totalResults > 0 && onPageChange && onPageSizeChange && <Pagination currentPage={currentPage} pageSize={pageSize} totalResults={totalResults} pageSizeOptions={pageSizeOptions} onPageChange={onPageChange} onPageSizeChange={onPageSizeChange} labels={labels} />;
    $[57] = currentPage;
    $[58] = labels;
    $[59] = onPageChange;
    $[60] = onPageSizeChange;
    $[61] = pageSize;
    $[62] = pageSizeOptions;
    $[63] = totalResults;
    $[64] = t26;
  } else {
    t26 = $[64];
  }
  let t27;
  if ($[65] !== t24 || $[66] !== t25 || $[67] !== t26) {
    t27 = <div className="flex-1 min-w-0">{t24}{t25}{t26}</div>;
    $[65] = t24;
    $[66] = t25;
    $[67] = t26;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  let t28;
  if ($[69] !== t23 || $[70] !== t27) {
    t28 = <div className="flex gap-5 lg:gap-10 flex-wrap">{t23}{t27}</div>;
    $[69] = t23;
    $[70] = t27;
    $[71] = t28;
  } else {
    t28 = $[71];
  }
  let t29;
  if ($[72] !== hasFilters || $[73] !== isMobile || $[74] !== labels?.labelForFilter || $[75] !== labels?.labelForSort || $[76] !== sortOptions) {
    t29 = isMobile && (hasFilters || sortOptions.length > 0) && <div className="fixed bottom-0 left-0 right-0 z-40 flex flex-col gap-2 p-4 bg-white border-t border-gray-m2 shadow-lg">{hasFilters && <button className="w-full py-3 bg-blue-trmb text-white text-sm font-bold uppercase tracking-wider rounded-lg border-none cursor-pointer flex items-center justify-center gap-2 hover:bg-blue-navy" onClick={() => openDrawer("filter")}><Icon type="t2u-collapse-close" size={14} />{labels?.labelForFilter || "Filter"}</button>}{sortOptions.length > 0 && <button className="w-full py-3 bg-white text-gray-m9 text-sm font-bold uppercase tracking-wider rounded-lg border border-gray-m3 cursor-pointer flex items-center justify-center gap-2 hover:bg-gray-m1" onClick={() => openDrawer("sort")}><Icon type="t2u-chevron-right" size={10} rotateDeg="270" /><Icon type="t2u-chevron-right" size={10} rotateDeg="90" />{labels?.labelForSort || "Sort"}</button>}</div>;
    $[72] = hasFilters;
    $[73] = isMobile;
    $[74] = labels?.labelForFilter;
    $[75] = labels?.labelForSort;
    $[76] = sortOptions;
    $[77] = t29;
  } else {
    t29 = $[77];
  }
  const t30 = currentSort || "";
  let t31;
  if ($[78] !== onFilterChange) {
    t31 = (groupId_0, values_0) => onFilterChange?.(groupId_0, values_0);
    $[78] = onFilterChange;
    $[79] = t31;
  } else {
    t31 = $[79];
  }
  let t32;
  if ($[80] !== onSortChange) {
    t32 = value => onSortChange?.(value);
    $[80] = onSortChange;
    $[81] = t32;
  } else {
    t32 = $[81];
  }
  let t33;
  if ($[82] !== onClearAllFilters) {
    t33 = () => onClearAllFilters?.();
    $[82] = onClearAllFilters;
    $[83] = t33;
  } else {
    t33 = $[83];
  }
  let t34;
  if ($[84] === Symbol.for("react.memo_cache_sentinel")) {
    t34 = () => setDrawerOpen(false);
    $[84] = t34;
  } else {
    t34 = $[84];
  }
  let t35;
  if ($[85] !== activeFilters || $[86] !== drawerMode || $[87] !== drawerOpen || $[88] !== filterGroups || $[89] !== labels || $[90] !== sortOptions || $[91] !== t30 || $[92] !== t31 || $[93] !== t32 || $[94] !== t33) {
    t35 = <MobileFilterDrawer isOpen={drawerOpen} mode={drawerMode} filterGroups={filterGroups} activeFilters={activeFilters} sortOptions={sortOptions} currentSort={t30} onFilterChange={t31} onSortChange={t32} onClearAll={t33} onClose={t34} labels={labels} />;
    $[85] = activeFilters;
    $[86] = drawerMode;
    $[87] = drawerOpen;
    $[88] = filterGroups;
    $[89] = labels;
    $[90] = sortOptions;
    $[91] = t30;
    $[92] = t31;
    $[93] = t32;
    $[94] = t33;
    $[95] = t35;
  } else {
    t35 = $[95];
  }
  let t36;
  if ($[96] !== t22 || $[97] !== t28 || $[98] !== t29 || $[99] !== t35 || $[100] !== wrapperClasses) {
    t36 = <div className={wrapperClasses}>{t22}{t28}{t29}{t35}</div>;
    $[96] = t22;
    $[97] = t28;
    $[98] = t29;
    $[99] = t35;
    $[100] = wrapperClasses;
    $[101] = t36;
  } else {
    t36 = $[101];
  }
  let t37;
  if ($[102] !== componentStyles || $[103] !== mainClasses || $[104] !== t36) {
    t37 = <div className={mainClasses} style={componentStyles} data-testid="layout-filterable-cards">{t36}</div>;
    $[102] = componentStyles;
    $[103] = mainClasses;
    $[104] = t36;
    $[105] = t37;
  } else {
    t37 = $[105];
  }
  return t37;
};
function _temp(btn, i) {
  return <TerraLink key={`btn-${btn.label}-${i}`} {...btn} />;
}
function _temp2(opt) {
  return <option key={opt.value} value={opt.value}>{opt.label}</option>;
}